import React, { useEffect, useMemo } from 'react';
import * as echarts from 'echarts';
import {
  useAppStateAssetPortfolioListStore,
  useAppStateCurrentAssetReportStore,
  useAppStateManageAssetPortfolioModalDataStore,
} from '../../../common/store/AppStateStore.zustand';
import { AssetPortfolio } from '../type/AssetPortfolio.type';
import AssetPortfolioCardPieChart from './AssetPortfolioCardPieChart';
import { getAssetPortfolioCardData } from '../util/AssetPortfolioUtils';
import { numberWithCommas, percentageString } from '../../../common/util/NumberUtils';

const AssetPortfolioCard = () => {
  const { manageAssetPortfolioModalData, setManageAssetPortfolioModalData } =
    useAppStateManageAssetPortfolioModalDataStore();
  const { assetPortfolioList } = useAppStateAssetPortfolioListStore();
  const { currentAssetReport } = useAppStateCurrentAssetReportStore();
  const [currentAssetPortfolio, setCurrentAssetPortfolio] = React.useState<AssetPortfolio>();

  useEffect(() => {
    if (assetPortfolioList.length > 0) {
      setCurrentAssetPortfolio(assetPortfolioList[0]);
    } else {
      setCurrentAssetPortfolio(undefined);
    }
  }, [assetPortfolioList]);

  const { assetPortfolioComponentDataList } = useMemo(() => {
    if (!currentAssetPortfolio) return { assetPortfolioComponentDataList: [] };
    return getAssetPortfolioCardData(currentAssetPortfolio, currentAssetReport);
  }, [currentAssetPortfolio, currentAssetReport]);

  const handleAssetPortfolioSelect = (assetPortfolioId: string) => {
    const selectedAssetPortfolio = assetPortfolioList.find((assetPortfolio) => assetPortfolio.id === assetPortfolioId);
    if (!selectedAssetPortfolio) {
      setCurrentAssetPortfolio(undefined);
      return;
    }
    setCurrentAssetPortfolio(selectedAssetPortfolio);
  };

  const handleAssetPortfolioClick = () => {
    setManageAssetPortfolioModalData({ isOpen: true, onClose: () => setManageAssetPortfolioModalData(undefined) });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all duration-300 group relative overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-purple-600/5 rounded-full -translate-y-32 translate-x-32 group-hover:translate-y-[-8rem] transition-transform duration-500"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-violet-600/5 rounded-full translate-y-32 -translate-x-32 group-hover:translate-y-[8rem] transition-transform duration-500"></div>
      <div className="flex justify-between items-center mb-6 relative">
        <div>
          <h3 className="text-xl font-semibold text-gray-800 mb-1 flex items-center">
            <span>포트폴리오 분석</span>
          </h3>
          <p className="text-sm text-gray-500">자산 배분 현황 및 목표</p>
        </div>
        <div>
          <select
            value={currentAssetPortfolio?.id}
            onChange={(e) => handleAssetPortfolioSelect(e.target.value)}
            className="appearance-none bg-white border border-gray-200 rounded-lg py-2 px-4 pr-8 text-gray-700 shadow-sm hover:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 cursor-pointer !rounded-button whitespace-nowrap transition-all duration-200"
          >
            {assetPortfolioList.map((assetPortfolio) => (
              <option value={assetPortfolio.id}>{assetPortfolio.name}</option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
            <i className="fas fa-chevron-down"></i>
          </div>
        </div>
      </div>
      {!currentAssetPortfolio && (
        <div className="flex flex-col items-center justify-center h-64 bg-gray-50/50 rounded-lg p-6">
          <div className="w-16 h-16 bg-purple-50 rounded-full flex items-center justify-center mb-4">
            <i className="fas fa-chart-pie text-3xl text-purple-300"></i>
          </div>
          <h4 className="text-lg font-medium text-gray-700 mb-2">포트폴리오가 없습니다</h4>
          <p className="text-sm text-gray-500 text-center max-w-sm">
            새로운 포트폴리오를 생성하여 자산을 효율적으로 관리해보세요
          </p>
          <button
            className="mt-6 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-all duration-200 flex items-center space-x-2 !rounded-button whitespace-nowrap"
            onClick={handleAssetPortfolioClick}
          >
            <i className="fas fa-plus"></i>
            <span>포트폴리오 추가하기</span>
          </button>
        </div>
      )}
      {currentAssetPortfolio && (
        <div className="flex flex-col items-center h-2/3">
          <div className="w-full">
            <AssetPortfolioCardPieChart assetPortfolioComponentDataList={assetPortfolioComponentDataList} />
          </div>
          <div className="w-full mt-4 md:mt-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="py-2 px-1 text-left font-medium">항목</th>
                  <th className="py-2 px-1 text-left font-medium">목표</th>
                  <th className="py-2 px-1 text-left font-medium">현재</th>
                  <th className="py-2 px-1 text-right font-medium">차이</th>
                </tr>
              </thead>
              <tbody>
                {assetPortfolioComponentDataList.map((assetPortfolioComponentData) => (
                  <tr className="border-b">
                    <td className="py-2 px-1">{assetPortfolioComponentData.name}</td>
                    <td className="py-2 px-1 text-left">
                      <span className="inline-block whitespace-nowrap mr-1">
                        {percentageString(assetPortfolioComponentData.targetRate)}
                      </span>
                      <span className="inline-block whitespace-nowrap">
                        ({numberWithCommas(assetPortfolioComponentData.targetValue)} 원)
                      </span>
                    </td>
                    <td className="py-2 px-1 text-left">
                      <span className="inline-block whitespace-nowrap mr-1">
                        {percentageString(assetPortfolioComponentData.actualRate)}
                      </span>
                      <span className="inline-block whitespace-nowrap">
                        ({numberWithCommas(assetPortfolioComponentData.value)} 원)
                      </span>
                    </td>
                    <td className="py-2 px-1 text-right">
                      <span className="inline-block whitespace-nowrap">
                        {numberWithCommas(assetPortfolioComponentData.targetValue - assetPortfolioComponentData.value)}{' '}
                        원
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetPortfolioCard;
